/**
 * PHASE 4 — device orientation model.
 *
 * Converts the W3C DeviceOrientationEvent alpha/beta/gamma values into
 * quaternions, following the specification exactly:
 *
 *   Z - X' - Y'' intrinsic Tait-Bryan angles, right-hand convention, applied
 *   to the device frame (x right, y top, z out of screen).
 *
 * The earth frame is the accelerometer spec's "Earth's reference coordinate
 * system": x = East, y = North, z = Up (right-handed ENU).
 *
 * `deviceToEarth(alpha, beta, gamma)` yields q with
 *   v_earth = rotateVector(v_device, q)
 * i.e. q is the "unit quaternion of the device in the earth frame", exactly
 * as derived in the spec appendix (A.2). Examples pinned by the spec text:
 *   - flat on table, top of screen pointing West  -> {alpha: 90, beta: 0, gamma: 0}
 *   - held upright, screen vertical, top up      -> beta = 90
 *   - compass heading = (360 - alpha) when flat
 */

import type { Quat } from "./quaternion";
import { mul, quat } from "./quaternion";

export interface OrientationReading {
  /** Rotation around device z axis, degrees, [0, 360). */
  alphaDeg: number | null;
  /** Rotation around the x' axis, degrees, [-180, 180). */
  betaDeg: number | null;
  /** Rotation around the y'' axis, degrees, [-90, 90). */
  gammaDeg: number | null;
  /** True when the values are absolute (references true/magnetic north). */
  absolute: boolean;
  timestampMs: number;
}

const DEG = Math.PI / 180;

/**
 * W3C spec A.2 formula (angles in degrees). Result is a unit quaternion.
 * When any angle is null the reading is unusable -> returns null.
 */
export function deviceToEarthQ(reading: OrientationReading): Quat | null {
  const { alphaDeg, betaDeg, gammaDeg } = reading;
  if (alphaDeg === null || betaDeg === null || gammaDeg === null) return null;
  const a = alphaDeg * DEG;
  const b = betaDeg * DEG;
  const g = gammaDeg * DEG;

  const cX = Math.cos(b / 2);
  const cY = Math.cos(g / 2);
  const cZ = Math.cos(a / 2);
  const sX = Math.sin(b / 2);
  const sY = Math.sin(g / 2);
  const sZ = Math.sin(a / 2);

  return quat(
    sX * cY * cZ - cX * sY * sZ,
    cX * sY * cZ + sX * cY * sZ,
    cX * cY * sZ + sX * sY * cZ,
    cX * cY * cZ - sX * sY * sZ,
  );
}

/** Inverse of deviceToEarthQ: v_device = rotateVector(v_earth, conj(q)). */
export function earthToDeviceQ(reading: OrientationReading): Quat | null {
  const q = deviceToEarthQ(reading);
  return q ? { x: -q.x, y: -q.y, z: -q.z, w: q.w } : null;
}

/**
 * Compass heading of the device on a horizontal surface, degrees [0, 360).
 * Spec: heading = (360 - alpha) when flat. Returns null when beta/gamma are
 * null or the device is far from flat (tilted more than `tiltLimitDeg`,
 * default 25) — where heading is ill-defined.
 */
export function compassHeading(reading: OrientationReading, tiltLimitDeg = 25): number | null {
  if (reading.alphaDeg === null || reading.betaDeg === null || reading.gammaDeg === null) return null;
  if (Math.abs(reading.betaDeg) > tiltLimitDeg || Math.abs(reading.gammaDeg) > tiltLimitDeg) return null;
  return (360 - reading.alphaDeg) % 360;
}

/** Compose deviceToEarth with an extra yaw rotation about device z. */
export function applyDeviceYaw(qDeviceToEarth: Quat, yawRad: number): Quat {
  // Rotating the device frame by yaw about its z axis: multiply on the right.
  return mul(qDeviceToEarth, quat(0, 0, Math.sin(yawRad / 2), Math.cos(yawRad / 2)));
}